import React, { useContext, useEffect, useState } from "react";
import { Form, Input, Button, Upload, Select, Alert } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import API from "../../../API";
import Loader from "../../Packages/componets/Loader";
import "./styles.css";
import { AppContext } from "../../../context/AppContext";
import { useAuth } from "../../../auth/AuthContext";
import * as AppPermissions from "../../../Util/permissions";
import { useCallback } from "react";

const { TextArea } = Input;

export const DOCUMENT_STATUS = [
  { value: "pending", label: "Pending" },
  { value: "approved", label: "Approve" },
];

export const MaxSizeMB = 100;

const AddDocumentForm = ({ onSubmit, setVisible, initialValues, formErrors }) => {
  const [form] = Form.useForm();
  const [documentType, setDocumentType] = useState([]);
  const [documentCategories, setDocumentCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [fileError, setFileError] = useState(null);
  const [projects, setProjects] = useState([]);
  const [procuringEntities, setProcuringEntities] = useState([]);
  const [apiErrors, setApiErrors] = useState({});


  const { permissions } = useContext(AppContext);
  const { user: authUser } = useAuth();

  const hasPermissions = (requiredPermissions) => {
    const userPermissions = permissions;
    const status = requiredPermissions?.every((permission) =>
      userPermissions?.includes(permission)
    );
    return status;
  };

   const getDocumentType = useCallback(() => {
    setIsLoading(true);
    API.get("/document-types")
      .then((res) => {
        setIsLoading(false);
        console.log({ res: res });
        setDocumentType(res);
      })
      .catch((err) => {
        console.log({ err });
        setIsLoading(false);
      });
  }, [setIsLoading]);

  const getDocumentCategory = useCallback(() => {
    setIsLoading(true);
    API.get("document-categories")
      .then((res) => {
        setIsLoading(false);
        console.log({ res: res });
        setDocumentCategories(res);
      })
      .catch((err) => {
        console.log({ err });
        setIsLoading(false);
      });
  }, [setIsLoading]);

  const setDefaultProcuringEntity = useCallback((project) => {
    const [{ procuring_entities, id }] = project;

    const councils = procuring_entities.map((entity) => {
      return {
        ...entity.council,
        procuring_entity_id: entity.procuring_entity_id,
      };
    });

    if (authUser?.councils && authUser.councils.length > 0) {
      setProcuringEntities(authUser.councils);
      return;
    }

    setProcuringEntities(
      councils.map((council) => ({
        council_id: council.id,
        name: council.name,
        id: council.procuring_entity_id,
        projectId: id,
      }))
    );
  }, [authUser]);

  const fetchProjects = useCallback(async () => {
    try {
      const response = await API.getPackageProjects();
      const projectNames = response.data.data.map((project) => project);

      setProjects(projectNames);

      if (initialValues) {
        setDefaultProcuringEntity(
          response.data.data.filter(
            (project) => project.id === initialValues.project_id
          )
        );
      }
    } catch (error) {
      console.error("Failed to fetch projects", error);
    }
  }, [initialValues, setDefaultProcuringEntity]);
  
  useEffect(() => {
    getDocumentType();
    getDocumentCategory();
    fetchProjects();
  }, [fetchProjects, getDocumentCategory, getDocumentType]);

  useEffect(() => {
    if (initialValues) {
      form.setFieldsValue(initialValues);
    }
    setFileError(null);
    setApiErrors({});
  }, [initialValues, form]);

  useEffect(() => {
    if (formErrors && Object.keys(formErrors).length > 0) {
      setApiErrors(formErrors);
      Object.keys(formErrors).forEach(fieldName => {
        if (fieldName !== 'file') {
          form.setFields([
            {
              name: fieldName,
              errors: formErrors[fieldName],
            },
          ]);
        }
      });
    }
  }, [formErrors, form]);

 

  const handleSubmit = (values) => {
    if (initialValues) {
      values.id = initialValues.id;
    }
    onSubmit(values, form);
  };

  const isFileSizeValid = (file) => {
    const maxSizeMB = MaxSizeMB;
    const fileSizeInMB = file.size / (1024 * 1024); // Convert file size from bytes to MB
    return fileSizeInMB <= maxSizeMB;
  };

  const isFileTypeValid = (file) => {
    const allowedTypes = ['jpg', 'jpeg', 'png', 'pdf', 'doc', 'docx', 'xls', 'xlsx'];
    const fileExtension = file.name.split('.').pop().toLowerCase();
    return allowedTypes.includes(fileExtension);
  };

  

  const handleProjectSelectChange = (projectId) => {
    const selectedProject = projects.filter(
      (project) => project.id === projectId
    );
    const [{ procuring_entities }] = selectedProject;

    const councils = procuring_entities.map((entity) => {
      return {
        ...entity.council,
        procuring_entity_id: entity.procuring_entity_id,
      };
    });

    if (authUser?.councils && authUser.councils.length > 0) {
      setProcuringEntities(authUser.councils);
      return;
    }
    setProcuringEntities(
      councils.map((council) => ({
        council_id: council.id,
        name: council.name,
        id: council.procuring_entity_id,
        projectId: projectId,
      }))
    );
  };

 

  return (
    <div className="document-form-container">
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        className="document-form"
        initialValues={initialValues}
      >
        <Form.Item
          label="Title"
          name="title"
          rules={[{ required: true, message: "Please enter a title" }]}
        >
          <Input placeholder="Enter Title" />
        </Form.Item>

        <Form.Item
          label="Document Description"
          name="description"
          rules={[
            {
              required: true,
              message: "Please enter a document description",
            },
          ]}
        >
          <TextArea rows={4} placeholder="Enter Document Description" />
        </Form.Item>

        <Form.Item label="Document Type" name="type">
          <Select
            style={{ width: "100%" }}
            placeholder="Enter Document Type"
            notFoundContent={null}
            options={documentType.map((category) => ({
              value: category.id,
              label: category.name,
            }))}
          ></Select>
        </Form.Item>

        <Form.Item label="Document Category/Section" name="category">
          <Select
            style={{ width: "100%" }}
            placeholder="Enter Document Category/Section"
            notFoundContent={null}
            options={documentCategories.map((category) => ({
              value: category.id,
              label: category.name,
            }))}
          ></Select>
        </Form.Item>

        <Form.Item label="Document Status" name="status">
          <Select
            style={{ width: "100%" }}
            placeholder="Update Document Status"
            notFoundContent={null}
            options={
              hasPermissions([AppPermissions.CAN_APPROVE_DOCUMENT])
                ? DOCUMENT_STATUS
                : DOCUMENT_STATUS.filter(
                    (status) => status.value !== "approved"
                  )
            }
          ></Select>
        </Form.Item>

        <Form.Item label="Project" name="project_id">
          <Select
            style={{ width: "100%" }}
            placeholder="Select Project"
            notFoundContent={null}
            onChange={(id) => handleProjectSelectChange(id)}
            options={projects.map((project, index) => ({
              value: project.id,
              label: project.name,
            }))}
          ></Select>
        </Form.Item>

        <Form.Item label="Councils" name="procuring_entity">
          <Select
            style={{ width: "100%" }}
            placeholder="Select Council"
            notFoundContent={null}
            options={procuringEntities.map((entity) => ({
              value: entity.id,
              label: entity.name,
            }))}
          ></Select>
        </Form.Item>
        <Form.Item
          label="Upload Document"
          name="upload"
          valuePropName="fileList"
          getValueFromEvent={(e) => (Array.isArray(e) ? e : e?.fileList)}
          rules={[
            {
              required: !!initialValues ? false : true,
              message: "Please upload a document",
            },
          ]}
        >
          <Upload
            name="document"
            listType="picture"
            className="upload-document"
            maxCount={1}
            beforeUpload={(file) => {
              setFileError(null); 
              
              if (!isFileTypeValid(file)) {
                setFileError(
                  "The file must be a file of type: jpg, jpeg, png, pdf, doc, docx, xls, xlsx."
                );
                return false;
              }
              
              if (!isFileSizeValid(file)) {
                setFileError(
                  `The file is too large. Maximum size allowed is ${MaxSizeMB} MB.`
                );
                return false;
              }

              return false;
            }} 
          >
            <Button icon={<UploadOutlined />}>Click to Upload</Button>
          </Upload>
        </Form.Item>
        <div style={{ marginTop: 10, marginBottom: 20 }}>
          {fileError && <Alert message={fileError} type="error" showIcon />}
          {apiErrors.file && apiErrors.file.length > 0 && (
            <Alert message={apiErrors.file[0]} type="error" showIcon />
          )}
        </div>
        <Form.Item>
          <Button
            className="prst-button"
            htmlType="submit"
            block
            disabled={isLoading}
            style={{
              opacity: isLoading ? 0.7 : 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {isLoading ? (
              <span style={{ display: "flex", alignItems: "center" }}>
                <Loader flexDirection="row" fontSize={16} width="auto" />
                Submitting...
              </span>
            ) : initialValues ? (
              "Update"
            ) : (
              "Submit"
            )}
          </Button>
        </Form.Item>
      </Form>
    </div>
  );
};

export default AddDocumentForm;
